import { cn } from '@w/ui/lib/utils'
import { EmojiPicker } from '@w/ui/components/emoji-picker'
import { Avatar } from '@w/ui/components/avatar'
import { useState } from 'react'
import { FileTextIcon } from '@w/ui/components/icon'
import { sidebarMenuButtonVariants } from '@w/ui/lib/sidebar-menu-button'
import { Button } from '@w/ui/components/button'
import { Input } from '@w/ui/components/input'
import { Popover, PopoverDescription, PopoverPopup, PopoverTitle, PopoverTrigger } from '@w/ui/components/popover'
import { Image } from '@w/ui/components/image'
import { chipVariants } from '@w/ui/components/chip'
import { relativeColorClasses, relativeColorName } from '@w/ui/lib/relative-color'
import { useMediaUrl } from '../../media-url-ctx'
import { usePageInfo } from '../../page-info-ctx'
import type { PageMeta } from '@w/content-blocks'
import type { BlockView, CoreViewApi } from '../view-api'
import { useBlockViewServices } from './services'
import { fieldEntries, TextField } from './field'
import { isListed } from '../../core/tree-read'

export const PageTitleView: BlockView = ({ block, api }) => {
  const { updateBlock } = useBlockViewServices()
  if (block.type !== 'pageTitle') return null
  const finishTitle = () => { if (!block.title.trim()) updateBlock(block.id, { title: api.t('contentBlocksEditor.document.untitledPage') }) }
  return <div contentEditable={false}><TextField as="h1" data-block-id={block.id} data-page-title="true" data-testid="content-blocks-editor-page-title"
    aria-label="Page title" aria-placeholder={api.t('contentBlocksEditor.document.untitledPage')}
    data-cb-field-placeholder={!block.title && api.mode === 'edit' ? api.t('contentBlocksEditor.document.untitledPage') : undefined}
    className="text-on-surface mb-6 min-h-12 w-full text-4xl leading-tight font-semibold text-balance outline-none"
    api={api} singleLine value={block.title} onChange={title => { updateBlock(block.id, { title }) }}
    onBlur={finishTitle}
    onFieldInput={type => {
      if (type !== 'insertParagraph' && type !== 'insertLineBreak') return false
      finishTitle()
      return enterBody(api)
    }} /></div>
}

/** The title is one plain line: Enter keeps it and moves the caret into the body (Lexical inserted a paragraph after the title). */
function enterBody(api: CoreViewApi) {
  const first = api.state.blocks.find(block => block.type !== 'pageTitle' && block.type !== 'pageMeta')
  // Selection paints into the DOM only while the editing root, not a field, holds focus.
  api.root?.focus()
  // An empty body gets its first paragraph from core, which authors one for a command on a body without text rows.
  if (!first) api.dispatch({ type: 'moveCaret', direction: 'backward', unit: 'document' })
  else if (first.type === 'p' && first.children.length === 0) {
    const point = { blockId: first.id, offset: 0 }
    api.select({ anchor: point, focus: point })
  } else api.dispatch({ type: 'insertBlockSibling', block: first.id, placement: 'before' })
  return true
}

export const PageMetaView: BlockView = ({ block, api }) => {
  const { updateBlock } = useBlockViewServices()
  const resolve = useMediaUrl()
  if (block.type !== 'pageMeta') return null
  const finishTitle = () => { if (!block.title.trim()) updateBlock(block.id, { title: api.t('contentBlocksEditor.document.untitledPage') }) }
  const cover = block.image ? resolve(block.image) : undefined
  const icon = block.icon ? resolve(block.icon) : undefined
  const tags = pageTags(block.tags)
  return <header hidden={api.mode === 'review' && !block.title?.trim() && !block.image && !block.icon && !block.emoji && tags.length === 0} data-block-id={block.id} data-page-meta="true" contentEditable={false} data-testid="content-blocks-editor-page-meta" className="relative">
    {cover && <div className="relative" contentEditable={false}>
      <Image alt="" className="w-full rounded-none sm:rounded-b-[28px]" data-testid="content-blocks-editor-page-cover" intrinsic src={cover} />
      {api.mode !== 'edit' && icon && !block.emoji && <Avatar alt="" className="absolute bottom-0 left-[12%] z-10 ml-1 -translate-x-1/2 translate-y-1/2 rounded-2xl bg-transparent"
        data-testid="content-blocks-editor-page-icon" name="" size="2xl" src={icon} />}
    </div>}
    {api.mode !== 'edit' && !cover && icon && !block.emoji && <div className="px-6 pt-10" contentEditable={false}>
      <Avatar alt="" className="rounded-2xl bg-transparent" data-testid="content-blocks-editor-page-icon" name="" size="2xl" src={icon} />
    </div>}
    {(api.mode === 'edit' || !!block.emoji || tags.length > 0) && <div data-page-meta-field="overlay" className={cover ? 'relative z-10 h-0' : 'px-6 pt-10'}>
      {api.mode === 'edit' ? <EmojiPicker value={block.emoji} imageSrc={icon} onValueChange={emoji => { updateBlock(block.id, { emoji: emoji || undefined, icon: undefined }) }}
        className={cover ? 'text-emoji-big absolute top-0 left-[12%] -translate-x-1/2 -translate-y-1/2' : 'text-emoji-big h-auto min-h-0 w-fit p-0'} />
        : block.emoji && <span aria-label="Page emoji" className={cover ? 'text-emoji-big absolute top-0 left-[12%] -translate-x-1/2 -translate-y-1/2' : 'text-emoji-big inline-block'}>{block.emoji}</span>}
      {tags.length > 0 && <div data-page-meta-field="tags" className={cover ? 'absolute top-0 right-6 flex -translate-y-1/2 gap-3' : 'mt-4 flex flex-wrap justify-center gap-3'}>
        {fieldEntries(tags, block.id).map(({ key, value: [tag, color], index }) => <TextField key={key} api={api} data-page-meta-field="tag" aria-label="Page tag" aria-placeholder="Tag"
          className={cn(chipVariants({ variant: 'tag' }), relativeColorClasses[relativeColorName(color)], 'outline-none')} value={tag}
          onChange={value => {
            const next = tags.map((entry, i) => i === index ? [value, color] : entry)
            updateBlock(block.id, { tags: typeof block.tags === 'string' ? next.map(entry => entry.join(':')).join(', ') : next })
          }} />)}
      </div>}
    </div>}
    <TextField as="h1" api={api} data-page-title="true" data-page-meta-field="title" data-testid="content-blocks-editor-page-meta-title"
      aria-label="Page metadata title" aria-placeholder={api.t('contentBlocksEditor.document.untitledPage')}
      data-cb-field-placeholder={!block.title && api.mode === 'edit' ? api.t('contentBlocksEditor.document.untitledPage') : undefined}
      className={`text-on-surface text-header-huge font-bolder mx-6 text-center tracking-wider text-balance outline-none ${cover ? 'pt-16' : icon || block.emoji ? 'mt-3' : 'pt-10'}`}
      value={block.title} onChange={title => { updateBlock(block.id, { title }) }} onBlur={finishTitle}
      onFieldInput={type => {
        if (type !== 'insertParagraph' && type !== 'insertLineBreak') return false
        finishTitle()
        return enterBody(api)
      }} />
  </header>
}

export function pageTags(tags: PageMeta['tags']): [string, string][] {
  if (!tags) return []
  if (Array.isArray(tags)) return tags
  return tags.split(',').flatMap(value => {
    const [tag, color] = value.split(':').map(part => part.trim())
    return tag ? [[tag, color || 'gray']] : []
  })
}

export const PageLinkView: BlockView = ({ block, api }) => {
  const { spaceId, resolvePage, onOpenPage } = usePageInfo()
  const { updateBlock } = useBlockViewServices()
  const [open, setOpen] = useState<string | null>(null)
  if (block.type === 'mention' || block.type === 'pages') return <TextField api={api} as="span"
    data-block-id={block.id} data-inline-presentation-type={block.type} data-inline-text-payload={JSON.stringify({ block })}
    aria-label={block.type === 'pages' ? 'Page references' : 'Mention'}
    className={block.type === 'pages' ? 'text-primary underline decoration-current/30 underline-offset-3'
      : 'bg-surface-2 text-primary box-decoration-clone rounded-md px-1.5 py-0.5'}
    value={block.type === 'pages' ? block.ids.join(', ') : block.subtype === 'date' ? new Date(block.date).toLocaleDateString() : `@${block.uid}`}
    onChange={value => {
      if (block.type === 'pages') updateBlock(block.id, { ids: value.split(',').map(id => id.trim()).filter(Boolean) })
      else if (block.subtype !== 'date') updateBlock(block.id, { uid: value.replace(/^@/u, '').trim() })
    }} />
  const ids = block.type === 'page' || block.type === 'pageLink' ? [block.pageId] : []
  const Tag = block.type === 'page' && isListed(api.state.blocks, block) ? 'div' : 'span'
  return <Tag data-block-id={block.id} contentEditable={false} data-inline-presentation-type={block.type}
    data-inline-text-payload={JSON.stringify({ block })}>
    {ids.map(pageId => {
      const ref = { pageId, spaceId: block.type === 'pageLink' ? block.spaceId ?? spaceId : spaceId }
      const page = resolvePage(ref)
      const label = page.label || api.t('contentBlocksEditor.document.pageUnavailable')
      const direct = block.type === 'pageLink' && !!onOpenPage
      return <Popover key={block.id} open={open === pageId} onOpenChange={value => {
        if (!value || !direct) setOpen(value ? pageId : null)
      }}>
        <PopoverTrigger render={block.type === 'page'
          ? <button type="button" aria-label={label} className={cn(sidebarMenuButtonVariants({ size: 'sm' }), 'text-on-surface min-w-0 rounded-lg px-1.5')} />
          : <Button type="button" variant="text" size="sm" className="text-primary mx-0.5 inline-flex h-auto min-h-0 px-1 py-0 align-baseline font-normal underline decoration-current/30 underline-offset-3" />}
          data-testid={`page-link-${pageId}`}
          onKeyDownCapture={event => { if (event.key === 'Enter' || event.key === ' ') event.stopPropagation() }}
          onClick={event => {
            if (!direct) return
            event.preventDefault()
            onOpenPage?.(ref)
          }}
          onContextMenu={event => {
            if (!direct || api.mode !== 'edit') return
            event.preventDefault()
            setOpen(pageId)
          }}
          >
          {block.type === 'page' ? <FileTextIcon aria-hidden={true} /> : page.emoji && <span aria-hidden>{page.emoji}</span>}<span>{label}</span>
        </PopoverTrigger>
        <PopoverPopup align="start" className="w-80"><div className="grid gap-4">
          <div className="grid gap-1"><PopoverTitle>Page link</PopoverTitle><PopoverDescription>Change target or open linked page.</PopoverDescription></div>
          {api.mode === 'edit' && <Input aria-label="Page id" data-testid={`page-id-${pageId}`} value={pageId}
            onInput={event => {
              const next = event.currentTarget.value
              updateBlock(block.id, { pageId: next })
              setOpen(next)
            }} />}
          {onOpenPage && <Button type="button" variant="outlined" onClick={() => { onOpenPage(ref); setOpen(null) }}>Open page</Button>}
        </div></PopoverPopup>
      </Popover>
    })}
  </Tag>
}
